import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PersonalInfoSection from './sections/PersonalInfoSection';
import LoginInfoSection from './sections/LoginInfoSection';
import ConsentSection from './sections/ConsentSection';
import ChangePasswordModal from '../ChangePasswordModal';
import WithdrawAccountModal from '../WithdrawAccountModal';
import { useAuthStore } from '../../../store/authStore';
import type { ProfileFormData } from '../../../types';

export interface ProfileFormProps {
  initialData?: ProfileFormData;
  onSubmit: (data: ProfileFormData) => Promise<void>;
  isLoading: boolean;
  hidePhoneVerification?: boolean;
  /** Show the 휴대폰 본인확인 (NICE) control under the phone field. */
  showIdentityVerification?: boolean;
  identityVerified?: boolean;
  /** Fired after a successful 본인확인 so the page can refetch. */
  onIdentityVerified?: () => void;
  influencerId?: string;
  oauthProvider?: string;
  kakaoLinked?: boolean;
  naverLinked?: boolean;
  googleLinked?: boolean;
  facebookLinked?: boolean;
  onSocialChanged?: () => void;
}

const DEFAULT_FORM_DATA: ProfileFormData = {
  name: '',
  nickname: '',
  phoneNumber: '',
  phoneVerified: false,
  marketingConsentCollection: false,
  marketingConsentPromotion: false,
  marketingConsentEmail: false,
  marketingConsentSMS: false,
};

/** Fields that the 기본정보 저장 button persists (used for the dirty check). */
const savedSnapshot = (d: ProfileFormData) =>
  JSON.stringify({
    name: d.name,
    nickname: d.nickname,
    marketingConsentCollection: d.marketingConsentCollection,
    marketingConsentPromotion: d.marketingConsentPromotion,
    marketingConsentEmail: d.marketingConsentEmail,
    marketingConsentSMS: d.marketingConsentSMS,
  });

const ProfileForm: React.FC<ProfileFormProps> = ({
  initialData,
  onSubmit,
  isLoading,
  hidePhoneVerification = false,
  showIdentityVerification = false,
  identityVerified = false,
  onIdentityVerified,
  influencerId,
  oauthProvider,
  kakaoLinked = false,
  naverLinked = false,
  googleLinked = false,
  facebookLinked = false,
  onSocialChanged,
}) => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [formData, setFormData] = useState<ProfileFormData>(
    initialData ?? DEFAULT_FORM_DATA
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  // Baseline of the last-saved values, for the dirty check on the save bar.
  const [savedBaseline, setSavedBaseline] = useState<string>(
    savedSnapshot(initialData ?? DEFAULT_FORM_DATA)
  );
  const isPhoneExisting = !!(initialData?.phoneNumber?.trim());

  // Sync form data only on initial load (when initialData transitions from undefined to a value)
  const initialDataLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (initialData && !initialDataLoadedRef.current) {
      initialDataLoadedRef.current = true;
      setFormData(initialData);
      setSavedBaseline(savedSnapshot(initialData));
    }
  }, [initialData]);

  const handleChange = useCallback(
    (field: string, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    []
  );

  const validate = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = t('myProfile.form.nameRequired');
    }
    // Promotion consent must have at least one delivery channel.
    if (
      formData.marketingConsentPromotion &&
      !formData.marketingConsentEmail &&
      !formData.marketingConsentSMS
    ) {
      newErrors.marketingChannel = t(
        'myProfile.form.channelRequired',
        '광고성 정보 수신 동의 시 이메일 또는 SMS 중 하나 이상을 선택해 주세요.',
      );
    }
    return newErrors;
  }, [formData.name, formData.marketingConsentPromotion, formData.marketingConsentEmail, formData.marketingConsentSMS, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const validationErrors = validate();
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        if (validationErrors.marketingChannel) {
          toast.error(validationErrors.marketingChannel);
        }
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit(formData);
        setSavedBaseline(savedSnapshot(formData));
        toast.success(t('myProfile.form.saveSuccess'));
      } catch {
        toast.error(t('myProfile.form.saveFailed'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, onSubmit, validate, t]
  );

  const submitting = isSubmitting || isLoading;
  const isDirty = savedSnapshot(formData) !== savedBaseline;

  return (
    <>
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        {/* Page heading */}
        <h1 className="text-[26px] sm:text-[28px] font-bold tracking-tight text-[#0F1E3D]">
          {t('myProfile.form.pageTitle', '내정보')}
        </h1>
        <p className="text-[13px] text-[#6B7794] mt-1 mb-6">
          {t('myProfile.form.pageSub', '인증·간편로그인 변경은 즉시 반영되고, 이름·닉네임·수신동의는 하단 기본정보 저장을 눌러야 저장됩니다.')}
        </p>

        {/* Card */}
        <div className="bg-white border border-[#E4E8F0] rounded-xl shadow-[0_1px_2px_rgba(15,30,61,.04),0_4px_12px_rgba(15,30,61,.05)] overflow-hidden">
          <PersonalInfoSection
            name={formData.name}
            nickname={formData.nickname}
            phoneNumber={formData.phoneNumber}
            phoneVerified={formData.phoneVerified}
            onChange={handleChange}
            errors={errors}
            isPhoneExisting={isPhoneExisting}
            hidePhoneVerification={hidePhoneVerification}
            showIdentityVerification={showIdentityVerification}
            identityVerified={identityVerified}
            onIdentityVerified={() => onIdentityVerified?.()}
          />

          <LoginInfoSection
            email={user?.email ?? ''}
            onChangePassword={() => setIsPasswordModalOpen(true)}
            userId={user?.userId}
            oauthProvider={oauthProvider}
            kakaoLinked={kakaoLinked}
            naverLinked={naverLinked}
            googleLinked={googleLinked}
            facebookLinked={facebookLinked}
            onChanged={onSocialChanged}
          />

          <ConsentSection
            marketingConsentCollection={formData.marketingConsentCollection}
            marketingConsentPromotion={formData.marketingConsentPromotion}
            marketingConsentEmail={formData.marketingConsentEmail}
            marketingConsentSMS={formData.marketingConsentSMS}
            onChange={handleChange}
          />

          {/* Card footer — 회원탈퇴 */}
          <div className="flex justify-end px-6 sm:px-8 py-4 border-t border-[#EEF1F6]">
            <button
              type="button"
              onClick={() => setIsWithdrawModalOpen(true)}
              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#4A5D8C] px-3 py-1.5 rounded-lg border border-[#E4E8F0] hover:text-[#F25151] hover:border-[#FF6B6B] hover:bg-[#FFE4E4] transition"
            >
              {t('myProfile.withdraw.entry', '회원탈퇴')}
            </button>
          </div>
        </div>

        {/* Save bar */}
        <div className="flex flex-col items-center gap-2 pt-7">
          <button
            type="submit"
            disabled={submitting || !isDirty}
            className="w-full max-w-[340px] px-6 py-3.5 text-sm font-bold text-white bg-[#0F1E3D] rounded-lg hover:bg-[#2D5BFF] disabled:bg-[#E4E8F0] disabled:text-[#A0AAC2] disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? t('myProfile.form.saving') : t('myProfile.form.save')}
          </button>
          <span className="text-[11.5px] text-[#6B7794]">
            {isDirty
              ? t('myProfile.form.unsavedHint', '저장하지 않은 변경 사항이 있습니다.')
              : t('myProfile.form.savedHint', '모든 변경 사항이 저장되었습니다.')}
          </span>
        </div>
      </form>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />

      {/* Withdraw Account Modal */}
      {user?.userId && (
        <WithdrawAccountModal
          isOpen={isWithdrawModalOpen}
          onClose={() => setIsWithdrawModalOpen(false)}
          userId={user.userId}
          influencerId={influencerId}
        />
      )}
    </>
  );
};

export default ProfileForm;
